import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config/supabase";
import {
  getCurrentWeekNumber,
  getRotationStartDate,
  getCurrentDayOfWeek,
  formatDate,
} from "../utils/dateCalculations";
import LoadingSpinner from "../components/LoadingSpinner";

interface MealAndSnack {
  meal: string | null;
  snack: string | null;
}

const HomePage: React.FC = () => {
  // Inizializza con una data che non sia weekend
  const getInitialDate = () => {
    const today = new Date();
    let initialDate = new Date(today);

    // Se oggi è weekend, va al venerdì precedente
    if (today.getDay() === 0) {
      // Domenica
      initialDate.setDate(today.getDate() - 2);
    } else if (today.getDay() === 6) {
      // Sabato
      initialDate.setDate(today.getDate() - 1);
    }

    return initialDate;
  };

  const [selectedDate, setSelectedDate] = useState<Date>(getInitialDate());
  const [mealData, setMealData] = useState<MealAndSnack>({
    meal: null,
    snack: null,
  });
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [todayFormatted, setTodayFormatted] = useState<string>("");

  // Funzioni per la navigazione delle date (saltando weekend)
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    do {
      newDate.setDate(newDate.getDate() - 1);
    } while (newDate.getDay() === 0 || newDate.getDay() === 6); // Salta domenica (0) e sabato (6)
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    do {
      newDate.setDate(newDate.getDate() + 1);
    } while (newDate.getDay() === 0 || newDate.getDay() === 6); // Salta domenica (0) e sabato (6)
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    let targetDate = new Date(today);

    // Se oggi è weekend (sabato o domenica), vai al venerdì precedente o lunedì successivo
    if (today.getDay() === 0) {
      // Se è domenica, vai al venerdì precedente
      targetDate.setDate(today.getDate() - 2);
    } else if (today.getDay() === 6) {
      // Se è sabato, va al venerdì precedente
      targetDate.setDate(today.getDate() - 1);
    }

    setSelectedDate(targetDate);
  };

  useEffect(() => {
    const fetchMealData = async () => {
      setLoading(true);
      try {
        const rotationStartDate = await getRotationStartDate();
        const weekNumber = getCurrentWeekNumber(
          rotationStartDate,
          selectedDate
        );
        const dayOfWeek = getCurrentDayOfWeek(selectedDate);

        setCurrentWeek(weekNumber);
        setTodayFormatted(formatDate(selectedDate));

        // Fetch pranzo del giorno selezionato
        const { data: menuData, error: menuError } = await supabase
          .from("menus")
          .select("meal")
          .eq("week_number", weekNumber)
          .eq("day_of_week", dayOfWeek)
          .single();

        // Fetch merenda del giorno selezionato
        const { data: snackData, error: snackError } = await supabase
          .from("snacks")
          .select("snack")
          .eq("day_of_week", dayOfWeek)
          .single();

        setMealData({
          meal: menuError ? null : menuData?.meal || null,
          snack: snackError ? null : snackData?.snack || null,
        });
      } catch (error) {
        console.error("Errore nel recupero dei dati:", error);
        setMealData({ meal: null, snack: null });
      } finally {
        setLoading(false);
      }
    };

    fetchMealData();
  }, [selectedDate]);

  // Funzione per determinare se la data selezionata è il giorno lavorativo più vicino a oggi
  const isToday = () => {
    const today = new Date();

    // Se oggi è giorni lavorativi (lunedì-venerdì), confronta direttamente
    if (today.getDay() >= 1 && today.getDay() <= 5) {
      return selectedDate.toDateString() === today.toDateString();
    }

    // Se oggi è weekend, non considerarlo come "oggi" nella logica
    return false;
  };

  const formatHeaderDate = () => {
    return isToday() ? `Oggi, ${todayFormatted}` : todayFormatted;
  };

  if (loading) {
    return <LoadingSpinner message="Caricamento menù..." />;
  }

  return (
    <div className="min-h-screen px-4 py-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Mensa Nido</h1>

        {/* Navigazione delle date */}
        <div className="flex items-center justify-center gap-4 mb-2">
          <button
            onClick={goToPreviousDay}
            className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            aria-label="Giorno precedente"
          >
            <span className="text-xl">←</span>
          </button>

          <div className="text-center">
            <p className="text-lg text-gray-600">{formatHeaderDate()}</p>
          </div>

          <button
            onClick={goToNextDay}
            className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            aria-label="Giorno successivo"
          >
            <span className="text-xl">→</span>
          </button>
        </div>

        {/* Pulsante Oggi */}
        {!isToday() && (
          <div className="mb-2">
            <button
              onClick={goToToday}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
            >
              Vai a oggi
            </button>
          </div>
        )}

        {currentWeek && (
          <p className="text-base text-gray-500 mt-1">
            Settimana {currentWeek}
          </p>
        )}
      </div>

      {/* Card Pranzo */}
      <div className="mb-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-soft-pink">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mr-4">
              <span className="text-2xl">🍽️</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Pranzo</h2>
          </div>
          {mealData.meal ? (
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {mealData.meal}
            </p>
          ) : (
            <p className="text-gray-500 italic">Informazione non disponibile</p>
          )}
        </div>
      </div>

      {/* Card Merenda */}
      <div className="mb-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-soft-yellow">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
              <span className="text-2xl">🍪</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Merenda</h2>
          </div>
          {mealData.snack ? (
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {mealData.snack}
            </p>
          ) : (
            <p className="text-gray-500 italic">Informazione non disponibile</p>
          )}
        </div>
      </div>

      {/* Links */}
      <div className="text-center space-y-4">
        <Link
          to="/menu"
          className="inline-block bg-pink-500 hover:bg-pink-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-lg"
        >
          Vedi Menù Completo
        </Link>

        {/* Link admin discreto */}
        <div className="mt-4">
          <Link
            to="/admin"
            className="text-gray-500 hover:text-gray-700 text-sm underline"
          >
            Area Admin
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
